
import { InventoryStats, InventoryItem } from '../page';

interface InventoryOverviewProps {
  stats: InventoryStats;
  items: InventoryItem[];
  onViewDetail: () => void;
  onViewAlerts: () => void;
}

export default function InventoryOverview({ stats, items, onViewDetail, onViewAlerts }: InventoryOverviewProps) {
  const formatPrice = (price: number) => {
    if (price >= 1000000000) {
      return (price / 1000000000).toFixed(1) + ' tỷ ₫';
    } else if (price >= 1000000) {
      return (price / 1000000).toFixed(0) + ' triệu ₫';
    }
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(price);
  };

  const getTopCategories = () => {
    return Object.entries(stats.categories)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 4);
  };

  const getLowStockItems = () => {
    return items
      .filter(item => item.status === 'low_stock' || item.status === 'out_of_stock')
      .sort((a, b) => a.quantity - b.quantity)
      .slice(0, 5);
  };

  const getTopValueItems = () => {
    return items
      .sort((a, b) => b.totalValue - a.totalValue)
      .slice(0, 5);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <i className="ri-shopping-bag-line text-orange-600 text-lg sm:text-xl"></i>
              </div>
            </div>
            <div className="ml-3 sm:ml-4 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Tổng sản phẩm</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.totalProducts}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-palette-line text-blue-600 text-lg sm:text-xl"></i>
              </div>
            </div>
            <div className="ml-3 sm:ml-4 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Tổng biến thể</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.totalVariants}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="ri-archive-line text-green-600 text-lg sm:text-xl"></i>
              </div>
            </div>
            <div className="ml-3 sm:ml-4 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Tổng tồn kho</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.totalQuantity}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-purple-600 text-lg sm:text-xl"></i>
              </div>
            </div>
            <div className="ml-3 sm:ml-4 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Giá trị kho</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{formatPrice(stats.totalValue)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alert Summary */}
      {(stats.lowStockItems > 0 || stats.outOfStockItems > 0) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start sm:items-center">
              <i className="ri-alarm-warning-line text-red-600 text-xl mr-3 mt-0.5 sm:mt-0"></i>
              <div>
                <h3 className="text-sm font-medium text-red-800">Cảnh báo tồn kho</h3>
                <p className="text-sm text-red-600 mt-1">
                  {stats.outOfStockItems > 0 && `${stats.outOfStockItems} sản phẩm hết hàng`}
                  {stats.outOfStockItems > 0 && stats.lowStockItems > 0 && ', '}
                  {stats.lowStockItems > 0 && `${stats.lowStockItems} sản phẩm sắp hết`}
                </p>
              </div>
            </div>
            <button
              onClick={onViewAlerts}
              className="w-full sm:w-auto bg-red-600 text-white hover:bg-red-700 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap"
            >
              Xem chi tiết
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Categories Distribution */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Phân bố theo danh mục</h3>
            <button
              onClick={onViewDetail}
              className="text-orange-600 hover:text-orange-700 text-sm font-medium"
            >
              Xem tất cả
            </button>
          </div>
          <div className="space-y-3">
            {getTopCategories().map(([category, count]) => (
              <div key={category} className="flex items-center justify-between">
                <span className="text-sm text-gray-600 truncate flex-1 mr-2">{category}</span>
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <div className="w-16 sm:w-20 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-orange-600 h-2 rounded-full"
                      style={{ width: `${(count / stats.totalProducts) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-gray-900 w-6 sm:w-8 text-right">{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Low Stock Items */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Sản phẩm cần nhập thêm</h3>
            <button
              onClick={onViewAlerts}
              className="text-red-600 hover:text-red-700 text-sm font-medium"
            >
              Xem tất cả
            </button>
          </div>
          <div className="space-y-3">
            {getLowStockItems().length > 0 ? (
              getLowStockItems().map((item) => (
                <div key={item.id} className="flex items-center space-x-3">
                  {item.image && (
                    <img
                      src={item.image}
                      alt={item.productName}
                      className="w-8 h-8 sm:w-10 sm:h-10 object-cover object-center rounded flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {item.productName}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {item.size} - {item.color}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className={`text-sm font-medium ${
                      item.quantity === 0 ? 'text-red-600' : 'text-orange-600'
                    }`}>
                      {item.quantity}
                    </span>
                    <p className="text-xs text-gray-500">còn lại</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                Tất cả sản phẩm đều có đủ tồn kho
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Top Value Items */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Sản phẩm có giá trị cao nhất</h3>
          <button
            onClick={onViewDetail}
            className="text-orange-600 hover:text-orange-700 text-sm font-medium"
          >
            Xem chi tiết
          </button>
        </div>
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-2 sm:px-0 text-xs sm:text-sm font-medium text-gray-600">Sản phẩm</th>
                  <th className="text-left py-2 px-2 text-xs sm:text-sm font-medium text-gray-600 hidden sm:table-cell">Size/Màu</th>
                  <th className="text-right py-2 px-2 text-xs sm:text-sm font-medium text-gray-600">SL</th>
                  <th className="text-right py-2 px-2 text-xs sm:text-sm font-medium text-gray-600 hidden sm:table-cell">Đơn giá</th>
                  <th className="text-right py-2 px-2 text-xs sm:text-sm font-medium text-gray-600">Tổng giá trị</th>
                </tr>
              </thead>
              <tbody>
                {getTopValueItems().map((item) => (
                  <tr key={item.id} className="border-b border-gray-100">
                    <td className="py-3 px-2 sm:px-0">
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        {item.image && (
                          <img
                            src={item.image}
                            alt={item.productName}
                            className="w-6 h-6 sm:w-8 sm:h-8 object-cover object-center rounded flex-shrink-0"
                          />
                        )}
                        <div className="min-w-0">
                          <span className="text-xs sm:text-sm font-medium text-gray-900 block truncate">{item.productName}</span>
                          <span className="text-xs text-gray-600 sm:hidden block truncate">{item.size} - {item.color}</span>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-2 text-xs sm:text-sm text-gray-600 hidden sm:table-cell">
                      {item.size} - {item.color}
                    </td>
                    <td className="py-3 px-2 text-xs sm:text-sm text-gray-900 text-right">{item.quantity}</td>
                    <td className="py-3 px-2 text-xs sm:text-sm text-gray-900 text-right hidden sm:table-cell">{formatPrice(item.unitPrice)}</td>
                    <td className="py-3 px-2 text-xs sm:text-sm font-medium text-gray-900 text-right">
                      {formatPrice(item.totalValue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Thao tác nhanh</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <button
            onClick={onViewDetail}
            className="flex items-center justify-center space-x-2 bg-orange-50 hover:bg-orange-100 text-orange-700 border border-orange-200 rounded-lg px-4 py-3 transition-colors"
          >
            <i className="ri-list-check-line text-lg"></i>
            <span className="font-medium text-sm sm:text-base">Xem chi tiết kho</span>
          </button>
          
          <button
            onClick={onViewAlerts}
            className="flex items-center justify-center space-x-2 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-lg px-4 py-3 transition-colors"
          >
            <i className="ri-alarm-warning-line text-lg"></i>
            <span className="font-medium text-sm sm:text-base">Cảnh báo tồn kho</span>
          </button>
          
          <button
            onClick={() => window.location.href = '/products'}
            className="flex items-center justify-center space-x-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg px-4 py-3 transition-colors"
          >
            <i className="ri-add-line text-lg"></i>
            <span className="font-medium text-sm sm:text-base">Thêm sản phẩm</span>
          </button>
        </div>
      </div>
    </div>
  );
}
